import React, { useState, useEffect } from "react";
import { getAbout, getDelegates } from "../api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import { Mail, Users, Heart, Target } from "lucide-react";

const values = [
  {
    icon: Users,
    title: "Proximité",
    description: "Toujours à l'écoute des salariés sur le terrain",
  },
  {
    icon: Heart,
    title: "Engagement",
    description: "Un dévouement sans faille pour vos intérêts",
  },
  {
    icon: Target,
    title: "Efficacité",
    description: "Des résultats concrets et mesurables",
  },
];

export default function NousConnaitre() {
  const [aboutText, setAboutText] = useState("");
  const [delegates, setDelegates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [aboutData, delegatesData] = await Promise.all([
          getAbout(),
          getDelegates(),
        ]);
        setAboutText(aboutData.text || "");
        setDelegates(delegatesData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div>
      {/* Page Header */}
      <section className="bg-gradient-to-br from-slate-50 via-sky-50/40 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-100 px-4 py-1.5 text-xs font-semibold mb-4">
            À propos
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            Nous connaître
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl">
            Découvrez qui nous sommes, nos valeurs et l'équipe qui se bat
            chaque jour pour vos droits.
          </p>
        </div>
      </section>

      {/* About Text */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : (
            <div className="prose prose-slate prose-lg max-w-none">
              {aboutText.split("\n\n").map((paragraph, index) => (
                <p
                  key={index}
                  className="text-slate-600 leading-relaxed mb-6 text-base"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-8 text-center">
            Nos valeurs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((value) => (
              <Card
                key={value.title}
                className="border-0 shadow-sm bg-white hover:shadow-md transition-all duration-300"
              >
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-sky-500" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-slate-500">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto" />

      {/* Delegates */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-3">
              Vos délégués syndicaux
            </h2>
            <p className="text-slate-500">
              Les personnes qui vous représentent et défendent vos intérêts
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {loading ? (
              <>
                {[1, 2].map((i) => (
                  <Card key={i} className="overflow-hidden border-0 shadow-sm">
                    <CardContent className="p-0">
                      <Skeleton className="aspect-square w-full" />
                      <div className="p-6 space-y-3">
                        <Skeleton className="h-5 w-32" />
                        <Skeleton className="h-6 w-48" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-56" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            ) : (
              delegates.map((delegate) => (
                <Card
                  key={delegate.id}
                  className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300 group"
                >
                  <CardContent className="p-0">
                    <div className="aspect-square overflow-hidden bg-slate-100">
                      <img
                        src={delegate.photo}
                        alt={delegate.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <Badge className="bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-100 text-xs mb-3">
                        {delegate.role}
                      </Badge>
                      <h3 className="text-xl font-bold text-slate-800 mb-1">
                        {delegate.name}
                      </h3>
                      <p className="text-sm text-slate-500 mb-4">
                        {delegate.bio}
                      </p>
                      <a
                        href={`mailto:${delegate.email}`}
                        className="inline-flex items-center gap-2 text-sm text-sky-500 hover:text-sky-600 font-medium transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        {delegate.email}
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
